import axios from "axios";
import {
  clearLocalStoragePreserveEssentials,
  handleCatchFromAPI,
  setConnectionNotif,
  URL_api,
} from "../../utility/util";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import {
  redirectToIdentityServerLogout,
  isIdentityServerEnabled,
  refreshAccessToken,
} from "../../utility/identityServerAuth";

// Fetch userInfo and tenantid from localStorage
const getUserInfo = () => JSON.parse(localStorage.getItem("userInfo") || "{}");
const getTenantId = () => localStorage.getItem("tenantid") || "";

// Create axios instance
const createAxiosInstance = () => {
  const userInfo = getUserInfo();
  const { uid = "", userRole: role = "", username = "", person = "" } = userInfo;

  return axios.create({
    baseURL: URL_api(),
    timeout: 300000,
    headers: {
      "Content-Type": "application/json",
      uid,
      role,
      tenantid: getTenantId(),
      username,
      person,
      "Access-Control-Allow-Origin": "*",
      Accept: "application/json, text/plain, */*",
    },
  });
};

const instance = createAxiosInstance();

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor
instance.interceptors.request.use(
  (config) => {
    // Read baseURL dynamically so changes to gatewayUri in localStorage
    // after module initialization are always reflected in every outgoing request.
    config.baseURL = URL_api();

    const token = Cookies.get("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // Read identity headers dynamically so changes made to localStorage
    // after module initialization (e.g. in Approvaldocument email-approval flow)
    // are always reflected in every outgoing request.
    const { uid = "", userRole: role = "", username = "", person = "" } = getUserInfo();
    config.headers["tenantid"] = getTenantId();
    config.headers["uid"] = uid;
    config.headers["role"] = role;
    config.headers["username"] = username;
    config.headers["person"] = person;

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const { response, message } = error;
    const userInfo = getUserInfo();

    if (response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return instance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        if (isIdentityServerEnabled()) {
          const tokenResponse = await refreshAccessToken();

          Cookies.set("token", tokenResponse.access_token, {
            expires: 1,
            secure: true,
            sameSite: "lax",
          });

          localStorage.setItem("expiresIn", tokenResponse.expires_in);
          localStorage.setItem("logintime", new Date());
          localStorage.setItem("tokenExpired", false);

          processQueue(null, tokenResponse.access_token);

          originalRequest.headers["Authorization"] = `Bearer ${tokenResponse.access_token}`;
          return instance(originalRequest);
        } else {
          const refreshToken = Cookies.get("refreshtoken");

          if (!refreshToken) {
            processQueue(new Error("No refresh token available"));

            clearLocalStoragePreserveEssentials();
            window.location.href = "/login";

            return Promise.reject(error);
          }

          // Remove existing connection
          const connection = setConnectionNotif(userInfo.username);
          if (connection) {
            await connection.invoke("RemoveConnection", connection.connectionId);
          }

          const response = await axios.post(
            `${URL_api()}api/Login/RefreshToken`,
            {},
            {
              params: { refreshToken },
            }
          );

          const { access_token, refresh_token, expires_in } = response.data.result.value.json;

          Cookies.set("token", access_token, { expires: 1, secure: true, sameSite: "lax" });
          Cookies.set("refreshtoken", refresh_token, { expires: 1, secure: true, sameSite: "lax" });

          localStorage.setItem("expiresIn", expires_in); // this is minutes 3600
          localStorage.setItem("logintime", new Date().toISOString());
          localStorage.setItem("tokenExpired", "false");

          processQueue(null, access_token);

          originalRequest.headers["Authorization"] = `Bearer ${access_token}`;
          return instance(originalRequest);
        }
      } catch (refreshError) {
        handleCatchFromAPI(refreshError, "axios/index.js", "RefreshToken");
        processQueue(refreshError, null);

        sessionStorage.setItem(
          "tempInfo",
          JSON.stringify({
            username: userInfo?.username,
            loginusingAD: userInfo?.loginusingAD,
          })
        );

        if (isIdentityServerEnabled()) {
          await redirectToIdentityServerLogout();
          return;
        } else {
          clearLocalStoragePreserveEssentials();
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (message === "Network Error") {
      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    }

    return Promise.reject(error);
  }
);

export default instance;
